import { useState } from "react";
import type { FormEvent } from "react";
import { useSession } from "../../lib/session";
import { getUserToken } from "../../lib/api";
import { Button, TextInput } from "../ui/primitives";
import { IconPlus } from "../ui/icons";
import { AuthModal } from "../components/AuthModal";

/**
 * The "new collection" form (today only the Collections hub renders it; admin has its
 * own create against adminApi). When `requireAuth` is set and the visitor is anonymous,
 * submitting opens the existing AuthModal (register) instead of creating — the same
 * anon-gate pattern the fap button uses — so signed-out users are prompted to make an
 * account. No analytics here: `collection_created` fires in userApi.createPlaylist, the
 * one layer all creation paths share — tracking here too double-counted the hub path.
 *
 * Guests never see the name field: it is unused before register, and at ~390px a fixed
 * "New collection…" input beside "+ Create account" collapsed and overflowed the hub
 * row (Casey 2026-10-02 AM). Guest path is a single Create-account control.
 */
export function CreateCollectionForm({
  onCreate,
  requireAuth = false,
  placeholder = "New collection…",
}: {
  onCreate: (name: string) => void | Promise<void>;
  requireAuth?: boolean;
  placeholder?: string;
}) {
  const user = useSession((s) => s.user);
  const ready = useSession((s) => s.ready);
  const [name, setName] = useState("");
  const [authOpen, setAuthOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    // Guest create is an account action (Rooms CTA pattern): open register even with an
    // empty name — requiring a typed name left "+ Create" disabled and the only copy
    // ("Sign in…") non-actionable (Casey 2026-09-28 midday).
    if (requireAuth && !user) {
      // Still waking (see `guest` below): neither create nor send a member to register.
      if (guest) setAuthOpen(true);
      return;
    }
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await onCreate(trimmed);
      setName("");
    } finally {
      setBusy(false);
    }
  }

  // A stored token whose /api/me has not answered yet is a member waking up, not a guest:
  // draw the member form from the first paint, or at 390 it grows ~50px under the heading
  // when /api/me lands and pushes the page down (collections layout shift, 2026-11-03).
  const guest = Boolean(requireAuth && !user && (ready || getUserToken() === null));

  return (
    <>
      <form
        onSubmit={submit}
        className={
          guest
            ? "flex shrink-0"
            : "flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center"
        }
      >
        {guest ? null : (
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={placeholder}
            className="min-w-0 w-full sm:w-48"
            maxLength={80}
          />
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={busy || (!guest && !name.trim())}
          className="shrink-0"
        >
          <IconPlus />
          {busy ? "…" : guest ? "Create account" : "Create"}
        </Button>
      </form>
      {authOpen ? (
        <AuthModal
          initialMode="register"
          reason="Create a free account to make your own collections."
          onClose={() => setAuthOpen(false)}
        />
      ) : null}
    </>
  );
}
